import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const ElectronicSignature3 = ({ onClose, onConfirm }) => {
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    setVisible(true);
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>전자서명 이미지 사용 및 활용 동의서</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <SectionContainer>
                <IntroText>
                  본인은 법무법인 더 에이치 황해에게 쿠팡 개인정보 유출
                  손해배상청구의 수행을 위임함에 있어, 스마트폰 등 전자기기
                  화면에 본인이 직접 기재한 자필 서명 이미지를 사용하는 것에
                  동의하며 다음과 같이 확인합니다.
                </IntroText>

                <Section>
                  <SectionTitle>자필 전자서명의 효력</SectionTitle>
                  <SectionContent>
                    본인은 전자기기 화면상에 입력한 본인의 자필 서명 이미지를
                    위임계약서, 소송위임장 등 사건 수행에 필요한 문서에
                    첨부·날인하는 것이, 종이 문서에 직접 자필 서명하거나
                    기명날인하는 것과 동일한 법적 효력을 가짐을 확인합니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>활용 권한의 위임</SectionTitle>
                  <SectionContent>
                    본인은 법무법인이 위 사건을 수행하는 목적 범위 내에서 본인의
                    자필 전자서명 이미지를 계약서 및 관련 법률 서면에 삽입,
                    편집, 제출하는 일에의 권한을 위임합니다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>본인 확인 및 동의</SectionTitle>
                  <SectionContent>
                    본 서명은 본인의 자유로운 의사에 따라 작성되었으며, 본인이
                    직접 정자(正字)로 기재하였음을 확인합니다.
                  </SectionContent>
                </Section>

                <SignatureDate>
                  {(() => {
                    const now = new Date();
                    return `${now.getFullYear()}년 ${
                      now.getMonth() + 1
                    }월 ${now.getDate()}일`;
                  })()}
                </SignatureDate>

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    위 내용을 모두 확인하고 동의합니다
                  </CheckboxLabel>
                </CheckboxContainer>
              </SectionContainer>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#0075ff")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const TermsTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const IntroText = styled.p`
  margin-bottom: 20px;
  line-height: 1.6;
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 25px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
`;

const ArticleTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;
  margin-bottom: 15px;
`;

const SignatureDate = styled.div`
  width: 100%;
  text-align: right;
  font-size: 14px;
  margin-top: 20px;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 20px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const FooterSection = styled.div`
  width: 100%;
  margin-top: 20px;
  text-align: center;
`;

const EnforcementDate = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
`;

export default ElectronicSignature3;
